import { Component, effect, inject, input } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Fluid } from 'primeng/fluid';
import { Message } from 'primeng/message';
import { TerminalModule } from 'primeng/terminal';
import { Tooltip } from 'primeng/tooltip';
import { IControllerDetail } from '../../../../models/controller';
import { Command } from '../../../../services/command';
import { Terminal } from './terminal/terminal';

type ExtTunnelAction = 'status' | 'start' | 'stop';
type MaintenanceAction = 'status' | 'start' | 'stop';
@Component({
  selector: 'app-tools',
  imports: [
    Card,
    Fluid,
    Button,
    Tooltip,
    TerminalModule,
    Message,
    Terminal,
    ConfirmDialog,
  ],
  providers: [ConfirmationService],
  templateUrl: './tools.html',
  styleUrl: './tools.css',
})
export class Tools {
  readonly commandService = inject(Command);
  controller = input.required<IControllerDetail>();

  private confirmationService = inject(ConfirmationService);

  externalInfoText: string = '';
  externalResponseText: string = '';
  currentExtAction: ExtTunnelAction | null = null;

  maintenanceInfoText: string = '';
  maintenanceResponseText: string = '';
  currentMaintenanceAction: MaintenanceAction | null = null;

  activated: boolean = false;
  canShowCommandsSection: boolean = false;

  constructor() {
    effect(() => {
      this.activated = this.controller().activated;
      this.canShowCommandsSection =
        this.controller().deviceClientVersion >= '2.2.1';
    });
  }

  getExtTunnelStatus() {
    this.currentExtAction = 'status';
    this.externalInfoText = 'Checking tunnel status...';
    this.externalResponseText = 'External tunnel status';

    this.commandService.getExtTunnelStatus(this.controller().serialNumber);
  }

  stopExtTunnel() {
    this.currentExtAction = 'stop';
    this.externalInfoText = 'Stopping tunnel...';
    this.externalResponseText = 'Stopping external tunnel';

    this.commandService.stopExtTunnel(this.controller().serialNumber);
  }

  startExtTunnel() {
    this.currentExtAction = 'start';
    this.externalInfoText = 'Starting tunnel...';
    this.externalResponseText = 'Starting external tunnel';

    this.commandService.startExtTunnel(this.controller().serialNumber);
  }

  getMaintenanceTunnelStatus() {
    this.currentMaintenanceAction = 'status';
    this.maintenanceInfoText = 'Checking tunnel status...';
    this.maintenanceResponseText = 'Maintenance tunnel status';

    this.commandService.getMaintenanceTunnelStatus(
      this.controller().serialNumber,
    );
  }

  stopMaintenanceTunnel() {
    this.currentMaintenanceAction = 'stop';
    this.maintenanceInfoText = 'Stopping tunnel...';
    this.maintenanceResponseText = 'Stopping maintenance tunnel';

    this.commandService.stopMaintenanceTunnel(this.controller().serialNumber);
  }

  startMaintenanceTunnel() {
    this.currentMaintenanceAction = 'start';
    this.maintenanceInfoText = 'Starting tunnel...';
    this.maintenanceResponseText = 'Starting maintenance tunnel';

    this.commandService.startMaintenanceTunnel(this.controller().serialNumber);
  }

  rebootBox() {
    this.commandService.rebootBox(this.controller().serialNumber);
  }

  getExternalStatusText() {
    const response = this.commandService.responseExternal();
    if (!response || !this.currentExtAction) return '';

    if (this.currentExtAction === 'status') {
      const text =
        response.status.enabled === false ? 'not connected 🔴' : 'connected 🟢';

      const addon = `The following information is used:
        URL: ${response.status.info.SERVER_URL}
        PORT: ${response.status.info.SERVER_PORT}`;

      return `${text}\n${addon}`;
    }

    if (this.currentExtAction === 'start') {
      return response.status === false ? 'failed 🔴' : 'started 🟢';
    }

    if (this.currentExtAction === 'stop') {
      return response.status === false ? 'failed 🔴' : 'stopped 🛑';
    }

    return '';
  }

  getMaintenanceStatusText() {
    const response = this.commandService.responseMaintenance();
    if (!response || !this.currentMaintenanceAction) return '';

    let status = false;
    if (response.status.status) {
      status = response.status.status;
    } else {
      status = response.status;
    }

    let info = '';
    if (response.status.info) {
      info = response.status.info;
    } else {
      info = response.info;
    }

    if (this.currentMaintenanceAction === 'status') {
      return status === false ? 'not connected 🔴' : 'connected 🟢';
    }

    if (this.currentMaintenanceAction === 'start') {
      const status2 = status === false ? 'failed 🔴' : 'started 🟢';

      return `${status2}\n${info}`;
    }

    if (this.currentMaintenanceAction === 'stop') {
      return status === false ? 'failed 🔴' : 'stopped 🛑';
    }

    return '';
  }

  getRebootBoxText() {
    return this.commandService.responseReboot().status.info;
  }

  getActivationText() {
    return this.commandService.responseActivation().status.info;
  }

  activationBoxConfirmation(event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `Should the external tunnel be started as well?`,
      header: 'Question',
      icon: 'fa-solid fa-circle-question',
      rejectButtonProps: {
        label: 'No, only activate this box',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Yes, start external communication',
        severity: 'primary',
      },
      accept: () => {
        this.activationBox(true);
      },
      reject: () => {
        this.activationBox(false);
      },
    });
  }

  activationBox(shouldStartExternalTunnel: boolean = false) {
    this.commandService.triggerActivation(
      this.controller().serialNumber,
      this.controller().ID,
      shouldStartExternalTunnel,
    );
  }
}
